import { makeAutoObservable, runInAction } from 'mobx'

import { LOADING_STATUS } from '../constants'
import type {
  TradeList,
  TradeStatusList,
  ResProductDetail,
  FavoriteResult,
  CommunityList,
  ResultRegisterContents,
} from '../constants'
import type { components, operations } from '../constants/schema'

import { HttpReq } from '../utils'

import RootStore from './root'
import { PagingSubStore, SingleSubStore } from './subStore'

type ProductDetail = components['schemas']['ProductDetailDto']

export class MarketStore {
  rootStore: RootStore
  productDetail: ProductDetail | null = null

  /* 작품 상세 */
  productDetailStore = new SingleSubStore<components['schemas']['ProductDetailDto']>(
    (param?: operations['ProductController_findOne']['parameters']['path']) => `/product/${param?.id}`,
  )

  /* 조각 투자 */
  productPagingStore = new PagingSubStore<components['schemas']['ProductDto']>('/product') // 리스트
  salePagingStore = new PagingSubStore<components['schemas']['ProductDetailDto']>('/product/sales') // 조각 투자 리스트

  /* 트레이드 */
  tradePagingStore = new PagingSubStore<TradeList>('/product/trades') // 트레이드 리스트
  tradeHistoryDonePagingStore = new PagingSubStore<TradeStatusList>( // 체결
    (param?: Record<string, any>) => `/product/${param?.productId}/trade/purchase`,
  )
  tradeHistoryYetPagingStore = new PagingSubStore<TradeStatusList>( // 미체결
    (param?: Record<string, any>) => `/product/${param?.productId}/trade/orders`,
  )

  /* 거래상태 */
  tradeStatusDonePagingStore = new PagingSubStore<TradeStatusList>('/trade/purchased') // 체결
  tradeStatusYetPagingStore = new PagingSubStore<TradeStatusList>('/trade/orders') // 미체결

  /* 커뮤니티 */
  communityPagingStore = new PagingSubStore<CommunityList>( // 작품 커뮤니티
    (param?: Record<string, any>) => `/product/${param?.productId}/post`,
  )

  /* 투자 적합성 테스트 리스트 */
  suitabilityPaperStore = new PagingSubStore<components['schemas']['SuitabilityDto']>(`/suitability`)

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  buyProduct = async (
    saleId: number,
    data: {
      count: components['schemas']['ReqQueryProductPurchase']['count']
      terms: components['schemas']['ReqQueryProductPurchase']['terms']
    },
  ): Promise<components['schemas']['UserProductDto'] | null> => {
    return await HttpReq<components['schemas']['UserProductDto']>({
      method: 'POST',
      url: `/product/${saleId}/purchase`,
      data,
      isLoadable: false,
    })
  }

  getProductDetail = async (productId: number): Promise<ProductDetail | null> => {
    try {
      const detail = await HttpReq<ProductDetail>({
        method: 'GET',
        url: `/product/${productId}`,
      })
      runInAction(() => {
        this.productDetail = detail
      })
      return detail
    } catch (error) {
      return null
    }
  }

  getProductTradeHistoryListYet = async (
    productId: number,
    data: Record<string, any>,
  ): Promise<ResProductDetail | null> => {
    return await HttpReq<ResProductDetail>({
      method: 'GET',
      url: `/product/${productId}/trade/orders`,
      data: data,
    })
  }

  /* 트레이드 거래 취소 */
  cancelTrade = async (productId: number, tradeOrderId: number, count: number) => {
    const cancelResult = await HttpReq<boolean>({
      method: 'DELETE',
      url: `/product/${productId}/trade/${tradeOrderId}`,
      data: {
        count: Number(count),
      },
    })
    return cancelResult
  }

  /* 작품 알림 설정하기 */
  favoriteProduct = async (productId: number) => {
    const favoriteResult = await HttpReq<FavoriteResult>({
      method: 'POST',
      url: `/product/${productId}/favorite`,
    })
    return favoriteResult?.favorite
  }

  /* 커뮤니티 좋아요 */
  likeContent = async (productId: number, communityId: number) => {
    await HttpReq<boolean>({
      method: 'POST',
      url: `/product/${productId}/post/${communityId}/like`,
    })
  }

  /* 커뮤니티 댓글 삭제 */
  deleteContent = async (productId: number, communityId: number) => {
    await HttpReq<boolean>({
      method: 'DELETE',
      url: `/product/${productId}/post/${communityId}`,
    })
  }

  /* 커뮤니티 댓글 작성 */
  registerContents = async (productId: number, contents: string) => {
    await HttpReq<ResultRegisterContents>({
      method: 'POST',
      url: `/product/${Number(productId)}/post`,
      data: { contents: contents },
    })
  }

  suitabilityPaper = async (data: components['schemas']['SuitabilityPaperDto']) => {
    await HttpReq<components['schemas']['SuitabilityResultListDto']>({
      method: 'POST',
      url: '/suitability/paper',
      data,
    })
  }
}
